import { useMemo } from 'react'
import { Stack } from '@mui/material'
import { Field, useForm, useFormState } from 'react-final-form'

import { useFormatMessage, useUserContext } from '@acre/utils'
import { MortgageReason, useGetReferralPartnersQuery } from '@acre/graphql'
import { COMPACT_LEFT_ALIGNED, Dropdown, WhenFieldChanges, YesNoRadioGroup } from '@acre/design-system'

import { messagePrefix } from './ReferClientModal.helpers'
import { getAdvisorOptions, getFirmOptions } from './ReferClientModalFields.helpers'

const ReferClientModalFields = () => {
  const form = useForm()
  const { values } = useFormState()
  const formatMessage = useFormatMessage()
  const user = useUserContext()

  const { data: referralPartnersData, loading: loadingReferralPartners } = useGetReferralPartnersQuery({
    variables: {
      includeUsers: true,
      input: {
        case_types: [MortgageReason.ReasonProtection],
        regulating_organisation: user?.organisation?.representative_of,
        all_users: true,
      },
    },
    skip: !user?.organisation?.representative_of,
  })

  const referralPartners = useMemo(
    () =>
      referralPartnersData?.getReferralPartners?.referral_partners
        ?.filter(
          (partner) =>
            partner?.paying_organisation_id &&
            partner?.paying_organisation_name &&
            partner?.paying_organisation_id !== user?.organisation_id,
        )
        .map((partner) => ({
          organisation_id: partner?.paying_organisation_id,
          organisation_name: partner?.paying_organisation_name,
          users: partner?.users,
        })) ?? [],
    [referralPartnersData?.getReferralPartners?.referral_partners, user?.organisation_id],
  )

  const selectedFirmUsers = useMemo(
    () => referralPartners?.find((org) => org.organisation_id === values?.refer_to_firm)?.users,
    [referralPartners, values?.refer_to_firm],
  )

  const firmOptions = useMemo(() => getFirmOptions(referralPartners), [referralPartners])

  const advisorOptions = useMemo(() => getAdvisorOptions(selectedFirmUsers), [selectedFirmUsers])

  return (
    <Stack gap={2}>
      <Field name="refer_to_firm">
        {({ input }) => {
          const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
            input.onChange(e)
            form.change('select_advisor', undefined)
          }
          return (
            <Dropdown
              {...input}
              onChange={handleChange}
              options={firmOptions}
              label={formatMessage(`${messagePrefix}.modal.referToFirm`)}
              variant={COMPACT_LEFT_ALIGNED}
              name="refer_to_firm"
              isLoading={loadingReferralPartners}
            />
          )
        }}
      </Field>
      <Field name="assign_to_advisor">
        {({ input }) => (
          <YesNoRadioGroup
            {...input}
            id="AssignToAdvisor"
            name="assign_to_advisor"
            label={formatMessage(`${messagePrefix}.modal.assignToAdvisor`)}
            variant={COMPACT_LEFT_ALIGNED}
          />
        )}
      </Field>
      <WhenFieldChanges
        field="assign_to_advisor"
        key="assign_to_advisor"
        becomes={!values?.assign_to_advisor}
        set="select_advisor"
        to={null}
      />
      {values?.assign_to_advisor && (
        <Field name="select_advisor">
          {({ input }) => (
            <Dropdown
              {...input}
              label={formatMessage(`${messagePrefix}.modal.selectAdvisor`)}
              name="select_advisor"
              options={advisorOptions}
              variant={COMPACT_LEFT_ALIGNED}
              isLoading={loadingReferralPartners}
            />
          )}
        </Field>
      )}
    </Stack>
  )
}

export default ReferClientModalFields
